import {
  ArrowRight,
  MapPin,
  MapTrifold,
  NavigationArrow,
  WarningCircle,
  X,
} from '@phosphor-icons/react'
import { useEffect, useState, type ComponentType } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { localizedCategoryName } from '@/lib/category-labels'
import { formatDate, formatTime } from '@/lib/i18n/format'
import { useAppLocale } from '@/features/discovery/hooks/ui'
import { currentCoordinates, geolocationErrorKey } from '@/features/discovery/geo'

import { MapHandoffSheet } from './map-handoff-sheet'
import { hasExactLocation } from './privacy'
import type { MapCenter, MapPanelProps, MapViewProps } from './types'

export function MapPanel({
  activities,
  selectedId,
  initialCenter,
  loading,
  error,
  onRetry,
  onSelect,
  onBoundsChange,
  onShowList,
}: MapPanelProps) {
  const { t } = useTranslation()
  const locale = useAppLocale()
  const [MapComponent, setMapComponent] =
    useState<ComponentType<MapViewProps> | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  )
  const [attempt, setAttempt] = useState(0)
  const [runtimeError, setRuntimeError] = useState<unknown>(null)
  const [focusPoint, setFocusPoint] = useState<MapCenter | null>(null)
  const [locating, setLocating] = useState(false)
  const [locationErrorKey, setLocationErrorKey] = useState<string | null>(null)
  const [handoffOpen, setHandoffOpen] = useState(false)

  // The first activity is only the initial camera hint; later bounds changes
  // must not remount or re-centre the map.
  const [startCenter] = useState<MapCenter | null>(() => {
    if (initialCenter) return initialCenter
    const first = activities.find(
      (activity) => activity.latitude !== null && activity.longitude !== null,
    )
    return first
      ? {
          latitude: first.latitude as number,
          longitude: first.longitude as number,
        }
      : null
  })

  useEffect(() => {
    let cancelled = false
    import('./map-view')
      .then((module) => {
        if (cancelled) return
        setMapComponent(() => module.MapView)
        setLoadState('ready')
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setRuntimeError(cause)
        setLoadState('error')
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const selected =
    activities.find((activity) => activity.id === selectedId) ?? null
  // Directions need the exact venue: only the creator and participants get it;
  // everyone else sees the privacy-jittered pin and area (D-082).
  const exactLocation = selected !== null && hasExactLocation(selected)

  async function locateMe() {
    if (locating) return
    setLocating(true)
    setLocationErrorKey(null)
    try {
      const coords = await currentCoordinates()
      setFocusPoint(coords)
    } catch (cause) {
      setLocationErrorKey(geolocationErrorKey(cause))
    } finally {
      setLocating(false)
    }
  }

  if (loadState === 'error' || runtimeError) {
    return (
      <MapFallback
        onRetry={() => {
          setRuntimeError(null)
          setMapComponent(null)
          setLoadState('loading')
          setAttempt((value) => value + 1)
        }}
        onShowList={onShowList}
      />
    )
  }

  return (
    <div className="map-panel overflow-hidden rounded-card border border-line bg-white">
      <div
        className="map-shell relative overflow-hidden"
        role="region"
        aria-label={t('map.regionLabel')}
      >
        {loadState === 'loading' || !MapComponent ? (
          <div className="map-skeleton" role="status" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            <span>{t('map.loading')}</span>
          </div>
        ) : (
          <MapComponent
            activities={activities}
            selectedId={selectedId}
            initialCenter={initialCenter ?? startCenter}
            focusPoint={focusPoint}
            onSelect={onSelect}
            onBoundsChange={onBoundsChange}
            onError={(cause) => setRuntimeError(cause)}
          />
        )}

        {initialCenter ? (
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            <button
              type="button"
              className="map-control"
              onClick={() => void locateMe()}
              disabled={locating}
              aria-busy={locating}
              aria-label={t('discovery.city.useLocation')}
            >
              <NavigationArrow size={20} weight="bold" aria-hidden="true" />
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="map-overlay" role="status" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            <span>{t('discovery.loading')}</span>
          </div>
        ) : null}

        {error && !loading ? (
          <div className="map-banner" role="alert">
            <span>{t('discovery.error.body')}</span>
            <button type="button" className="btn-ghost min-h-9" onClick={onRetry}>
              {t('common.retry')}
            </button>
          </div>
        ) : null}

        {!loading && !error && activities.length === 0 ? (
          <p className="map-empty-pill" role="status">
            <MapTrifold size={16} weight="bold" aria-hidden="true" />
            {t('map.empty.title')}
          </p>
        ) : null}

        {locationErrorKey ? (
          <p className="map-location-error" role="alert">
            {t(locationErrorKey)}
          </p>
        ) : null}

        {selected ? (
          <div className="map-preview">
            <button
              type="button"
              className="absolute top-1 right-1 grid size-9 place-items-center rounded-full text-deep hover:bg-mint"
              onClick={() => onSelect(null)}
            >
              <X size={18} weight="bold" aria-hidden="true" />
              <span className="sr-only">{t('common.close')}</span>
            </button>
            <p
              className="inline-flex rounded-full px-2 py-0.5 text-xs font-bold text-deep"
              style={{
                backgroundColor: selected.categoryColor
                  ? `color-mix(in srgb, ${selected.categoryColor} 25%, var(--paper))`
                  : 'var(--mint)',
              }}
            >
              {localizedCategoryName(
                t,
                selected.categorySlug,
                selected.categoryName,
              )}
            </p>
            <p className="mt-1 pr-8 font-head text-lg leading-none font-extrabold tracking-[-0.02em]">
              {selected.title}
            </p>
            <p className="mt-1 text-sm text-muted">
              {selected.startAt
                ? `${formatDate(selected.startAt, locale)} · ${formatTime(selected.startAt, locale)}`
                : null}
            </p>
            {!['creator', 'joined', 'attended'].includes(
              selected.viewerStatus ?? '',
            ) ? (
              <p className="text-xs font-semibold text-muted">
                {t('map.approximateArea')}
              </p>
            ) : null}
            {selected.locationName || selected.city ? (
              exactLocation ? (
                <button
                  type="button"
                  className="mt-1 flex min-h-9 items-center gap-1 text-left text-sm font-semibold text-teal"
                  onClick={() => setHandoffOpen(true)}
                  disabled={
                    selected.latitude === null || selected.longitude === null
                  }
                >
                  <MapPin size={15} weight="bold" aria-hidden="true" />
                  {selected.locationName || selected.city}
                </button>
              ) : (
                <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-muted">
                  <MapPin size={15} weight="bold" aria-hidden="true" />
                  {selected.locationName || selected.city}
                </p>
              )
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Link
                to={`/activity/${selected.id}`}
                className="inline-flex min-h-10 items-center gap-1 text-sm font-bold text-teal"
              >
                {t('discovery.card.details')}
                <ArrowRight size={15} weight="bold" aria-hidden="true" />
              </Link>
              {exactLocation ? (
                <button
                  type="button"
                  className="btn-secondary min-h-10 px-3 text-sm"
                  onClick={() => setHandoffOpen(true)}
                  disabled={
                    selected.latitude === null || selected.longitude === null
                  }
                >
                  <NavigationArrow size={15} weight="bold" aria-hidden="true" />
                  {t('map.directions')}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <MapHandoffSheet
        open={handoffOpen && selected !== null}
        onClose={() => setHandoffOpen(false)}
        title={selected?.title ?? ''}
        latitude={selected?.latitude ?? null}
        longitude={selected?.longitude ?? null}
      />
    </div>
  )
}

function MapFallback({
  onRetry,
  onShowList,
}: {
  onRetry: () => void
  onShowList: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="flex min-h-[320px] flex-col items-center justify-center rounded-card border border-line bg-white px-6 py-10 text-center"
      role="status"
    >
      <span className="grid size-14 place-items-center rounded-full bg-danger-soft text-danger">
        <WarningCircle size={28} weight="bold" aria-hidden="true" />
      </span>
      <h2 className="mt-4 text-2xl">{t('map.fallback.title')}</h2>
      <p className="mt-2 max-w-md text-muted">{t('map.fallback.body')}</p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <button type="button" className="btn-secondary" onClick={onRetry}>
          {t('common.retry')}
        </button>
        <button type="button" className="btn-primary" onClick={onShowList}>
          {t('map.showList')}
        </button>
      </div>
    </div>
  )
}
